import { randomUuid } from '../../../api/active/randomUuid';
import type { ActiveRequestInit } from '../../../api/active/roleViewDispatch';
import type { ActiveTransport } from '../../../api/active/transport';
import type { CatalogUploadPresignRequest } from '../../../catalog-media/catalogMediaApi';
import { fileSha256 } from '../../../catalog-media/fileDigest';
import {
  getLocalDemoMediaObject,
  getLocalDemoMediaPreview,
  type LocalDemoMediaRecord,
  saveLocalDemoMediaObject,
} from './localDemoMediaStore';

const MAX_BYTES = 10 * 1024 * 1024;
const PURPOSES = new Set([
  'product_image',
  'package_cover_image',
  'package_lighting_image',
  'plan_image',
  'plan_case_image',
]);
const demo = () =>
  import.meta.env.DEV &&
  (import.meta.env as ImportMetaEnv & { VITE_CPQ_OFFLINE_DEMO?: string })
    .VITE_CPQ_OFFLINE_DEMO === 'true' &&
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

function response(value: unknown, status = 200, etag = '"local-demo-v1"') {
  return new Response(JSON.stringify(value), {
    status,
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ETag: etag,
      'Cache-Control': 'no-store',
    },
  });
}

function problem(status: number, code: string, title: string) {
  return response({ code, title }, status);
}

interface Ticket {
  objectId: string;
  uploadUrl: string;
  expiresAt: string;
  rowVersion: string;
  uploadMethod: 'PUT';
  requiredHeaders: [];
  fileName: string;
  mediaType: CatalogUploadPresignRequest['mediaType'];
  byteSize: number;
  sha256: string;
  purpose: CatalogUploadPresignRequest['purpose'];
}

interface Operation {
  id: string;
  resourceId: string;
  createdAt: string;
  completedAt: string;
  attemptNo: number;
  rowVersion: string;
  type: 'file_scan';
}

/** Local image ticket, object store and CLEAN scan. It never calls fetch. */
export function createLocalMediaTransport(base: ActiveTransport) {
  const tickets = new Map<string, Ticket>();
  const operations = new Map<string, Operation>();
  const operationByObject = new Map<string, string>();

  const local = {
    ...base,
    localDemoMedia: true as const,
    async putLocalDemoObject(
      objectId: string,
      file: File,
      signal?: AbortSignal,
    ): Promise<string> {
      if (!demo()) throw new Error('本地图片演练仅限本机开发模式');
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const ticket = tickets.get(objectId);
      if (!ticket) throw new Error('本地图片上传票据不存在或已过期');
      if (file.size !== ticket.byteSize || file.type !== ticket.mediaType)
        throw new Error('所选图片与本地演练上传票据不一致');
      if ((await fileSha256(file)) !== ticket.sha256)
        throw new Error('图片 SHA-256 校验失败');
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const record: LocalDemoMediaRecord = {
        objectId,
        fileName: ticket.fileName,
        mediaType: ticket.mediaType,
        byteSize: file.size,
        sha256: ticket.sha256,
        purpose: ticket.purpose,
        blob: new Blob([await file.arrayBuffer()], { type: file.type }),
        status: 'uploaded',
      };
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      await saveLocalDemoMediaObject(record);
      return `local-etag-${file.size}-${ticket.sha256.slice(0, 12)}`;
    },
    async requestResponse(path: string, init: ActiveRequestInit = {}) {
      if (!demo()) return base.requestResponse(path, init);
      const method = (init.method ?? 'GET').toUpperCase();
      const url = new URL(path, window.location.origin);
      if (url.pathname === '/files/upload-tickets' && method === 'POST') {
        let body: CatalogUploadPresignRequest;
        try {
          body = JSON.parse(
            String(init.body ?? '{}'),
          ) as CatalogUploadPresignRequest;
        } catch {
          return problem(400, 'INVALID_BODY', '上传参数格式错误');
        }
        if (
          !body.fileName?.trim() ||
          !PURPOSES.has(body.purpose) ||
          !['image/jpeg', 'image/png', 'image/webp'].includes(body.mediaType) ||
          !Number.isSafeInteger(body.byteSize) ||
          body.byteSize < 1 ||
          body.byteSize >= MAX_BYTES ||
          !/^[a-f\d]{64}$/i.test(body.sha256)
        )
          return problem(
            422,
            'UPLOAD_VALIDATION_FAILED',
            '图片格式或大小不符合要求',
          );
        const objectId = `local-media-${randomUuid()}`;
        const ticket: Ticket = {
          objectId,
          uploadUrl: `cpq-local-demo://${objectId}`,
          expiresAt: new Date(Date.now() + 10 * 60_000).toISOString(),
          rowVersion: '1',
          uploadMethod: 'PUT',
          requiredHeaders: [],
          fileName: body.fileName,
          mediaType: body.mediaType,
          byteSize: body.byteSize,
          sha256: body.sha256,
          purpose: body.purpose,
        };
        tickets.set(objectId, ticket);
        const {
          fileName: _fileName,
          mediaType: _mediaType,
          byteSize: _byteSize,
          sha256: _sha256,
          purpose: _purpose,
          ...session
        } = ticket;
        return response(session);
      }
      const complete = /^\/files\/([^/]+):complete$/.exec(url.pathname);
      if (complete && method === 'POST') {
        const objectId = decodeURIComponent(complete[1]);
        const ticket = tickets.get(objectId);
        const record = await getLocalDemoMediaObject(objectId);
        if (!ticket || !record || record.status !== 'uploaded')
          return problem(409, 'UPLOAD_NOT_READY', '图片尚未完成本地上传');
        let body: { etag?: string; sha256?: string };
        try {
          body = JSON.parse(String(init.body ?? '{}')) as typeof body;
        } catch {
          return problem(400, 'INVALID_BODY', '图片完成信息格式错误');
        }
        const expectedEtag = `local-etag-${record.byteSize}-${ticket.sha256.slice(0, 12)}`;
        if (body.etag !== expectedEtag || body.sha256 !== ticket.sha256)
          return problem(422, 'UPLOAD_CHECKSUM_MISMATCH', '图片校验信息不一致');
        const clean: LocalDemoMediaRecord = { ...record, status: 'clean' };
        await saveLocalDemoMediaObject(clean);
        const operationId = `local-scan-${randomUuid()}`;
        const now = new Date().toISOString();
        operations.set(operationId, {
          id: operationId,
          resourceId: objectId,
          createdAt: now,
          completedAt: now,
          attemptNo: 1,
          rowVersion: '1',
          type: 'file_scan',
        });
        operationByObject.set(objectId, operationId);
        tickets.delete(objectId);
        return response({ operationId, status: 'accepted' }, 202);
      }
      const operationMatch = /^\/operations\/([^/]+)$/.exec(url.pathname);
      if (operationMatch && method === 'GET') {
        const operationId = decodeURIComponent(operationMatch[1]);
        const operation = operations.get(operationId);
        if (!operation) return base.requestResponse(path, init);
        const previewUrl = await getLocalDemoMediaPreview(operation.resourceId);
        return response({
          ...operation,
          status: previewUrl ? 'SUCCEEDED' : 'FAILED',
          ...(previewUrl
            ? { resultUrl: previewUrl }
            : { errorCode: 'LOCAL_IMAGE_MISSING' }),
        });
      }
      return base.requestResponse(path, init);
    },
  };
  return local;
}
